import { useState, useMemo } from "react";
import { CURRENCIES, POPULAR_CURRENCY_CODES } from "@/lib/currencies";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown, Search } from "lucide-react";

export function CurrencySelect({ value, onValueChange, className = "w-28" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) {
      // Show popular first, then rest
      const popular = CURRENCIES.filter(c => POPULAR_CURRENCY_CODES.includes(c.code));
      const rest = CURRENCIES.filter(c => !POPULAR_CURRENCY_CODES.includes(c.code));
      return { popular, rest };
    }
    const all = CURRENCIES.filter(
      c => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
    return { popular: [], rest: all };
  }, [search]);

  const selected = CURRENCIES.find(c => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between font-normal ${className}`}
          data-testid="currency-select"
        >
          <span>{value || "EUR"}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Währung suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="currency-search"
            />
          </div>
        </div>
        <ScrollArea className="h-[240px]">
          <div className="p-1">
            {filtered.popular.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Häufig verwendet</div>
                {filtered.popular.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => { onValueChange(c.code); setOpen(false); setSearch(""); }}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-muted cursor-pointer ${
                      value === c.code ? "bg-muted" : ""
                    }`}
                  >
                    <Check className={`h-3.5 w-3.5 ${value === c.code ? "opacity-100" : "opacity-0"}`} />
                    <span className="font-mono font-medium w-10">{c.code}</span>
                    <span className="text-muted-foreground truncate">{c.name}</span>
                  </button>
                ))}
                <div className="my-1 border-t" />
                <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Alle Währungen</div>
              </>
            )}
            {filtered.rest.map((c) => (
              <button
                key={c.code}
                onClick={() => { onValueChange(c.code); setOpen(false); setSearch(""); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-muted cursor-pointer ${
                  value === c.code ? "bg-muted" : ""
                }`}
              >
                <Check className={`h-3.5 w-3.5 ${value === c.code ? "opacity-100" : "opacity-0"}`} />
                <span className="font-mono font-medium w-10">{c.code}</span>
                <span className="text-muted-foreground truncate">{c.name}</span>
              </button>
            ))}
            {filtered.popular.length === 0 && filtered.rest.length === 0 && (
              <div className="px-2 py-4 text-sm text-center text-muted-foreground">
                Keine Währung gefunden
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
